import {
  Check,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  MessageSquareWarning,
  Upload,
} from "lucide-react"
import * as React from "react"

import { Button } from "@/components/ui/button"
import { Dialog, DialogContent } from "@/components/ui/dialog"
import { cn } from "@/lib/utils"

import type { FindingId, Message } from "../../data/review-data"
import { hasClientResponse, type ReportFindingItem } from "../report-data"

import { FindingCard } from "./finding-card"
import { FocusedLabel } from "./focused-label"

export interface ChecklistViewProps {
  /** The printed label. Empty when the review has none attached. */
  documentUrl: string
  /** The report's findings in label order, with every located occurrence joined in. */
  findings: readonly ReportFindingItem[]
  messagesFor: (findingId: FindingId) => readonly Message[]
  unreadFor: (findingId: FindingId) => number
  /**
   * Omitted, with `onMarkRead`, for a reader who may read the conversation but
   * not take part in it (a viewer). They still open the thread, read-only: it
   * is not marked read on opening, because that is a write the server refuses
   * them, and it has no message box.
   */
  onSendMessage?: ((findingId: FindingId, body: string) => void) | undefined
  onMarkRead?: ((findingId: FindingId) => void) | undefined
  /** Omitted for a reader who may not record the client's response — the controls go with it. */
  onSetResolution?:
    ((findingId: FindingId, resolution: "done" | "disputed" | null) => void) | undefined
  /** Omitted for a reader who may not file a corrected label. */
  onResubmit?: ((file: File) => void) | undefined
  resubmitPending?: boolean
  resubmitted?: boolean
  resubmitError?: string | null
  /**
   * A finding to bring up — set from outside the walkthrough, such as the
   * conversation panel. Jumped to once it is set; `onFocusHandled` is then
   * told, so the same finding can be asked for again.
   */
  focusFindingId?: FindingId | null
  onFocusHandled?: () => void
}

/**
 * The client's guided walkthrough: one divergence at a time, the label zoomed
 * to it, and a single response per step — "I have seen this and will fix it",
 * or "I dispute this".
 *
 * Both responses are the server's `client_resolution`, so they are the client's
 * actual acknowledgement and the review team sees them. Once every finding has
 * a response, the corrected label can be filed as the next version.
 */
export function ChecklistView({
  documentUrl,
  findings,
  messagesFor,
  unreadFor,
  onSendMessage,
  onMarkRead,
  onSetResolution,
  onResubmit,
  resubmitPending = false,
  resubmitted = false,
  resubmitError = null,
  focusFindingId = null,
  onFocusHandled,
}: ChecklistViewProps): JSX.Element {
  const total = findings.length
  const [index, setIndex] = React.useState(0)

  React.useEffect(() => {
    if (!focusFindingId) return
    const at = findings.findIndex((f) => f.id === focusFindingId)
    if (at >= 0) setIndex(at)
    onFocusHandled?.()
  }, [focusFindingId, findings, onFocusHandled])
  const [occurrenceIndex, setOccurrenceIndex] = React.useState(0)
  const [showMessages, setShowMessages] = React.useState(false)

  // A step can disappear under you — a finding is corrected, or the review re-run.
  const safeIndex = Math.min(index, Math.max(0, total - 1))
  const current: ReportFindingItem | undefined = findings[safeIndex]
  const currentId = current?.id ?? null

  React.useEffect(() => {
    setShowMessages(false)
    setOccurrenceIndex(0)
  }, [currentId])

  const respondedCount = findings.filter(hasClientResponse).length
  const everyFindingAnswered = total > 0 && respondedCount === total

  const isDone = React.useCallback(
    (id: FindingId) => findings.find((f) => f.id === id)?.clientResolution === "done",
    [findings],
  )

  const go = (next: number): void => {
    setIndex(Math.min(Math.max(next, 0), Math.max(0, total - 1)))
  }

  const toggleMessages = (): void => {
    if (!current) return
    // Marking read is a write; a read-only reader opens the thread without it.
    if (!showMessages) onMarkRead?.(current.id)
    setShowMessages(!showMessages)
  }

  if (total === 0 || !current) {
    return (
      <div className="mx-auto max-w-md rounded-lg border border-master/30 bg-master-tint p-8 text-center">
        <CheckCircle2 className="mx-auto size-8 text-master" aria-hidden="true" />
        <p className="mt-3 text-heading text-master-strong">Nothing left to work through</p>
        {/* True of all three ways the list empties: corrected, never found, or
            never checked. "Every divergence resolved" would assert a history
            the last two did not have. */}
        <p className="mt-1 text-body-sm text-master">
          There are no outstanding divergences on this version.
        </p>
      </div>
    )
  }

  const done = current.clientResolution === "done"
  const disputed = current.clientResolution === "disputed"

  return (
    <div className="flex w-full flex-col gap-4">
      <div className="flex justify-end">
        <div
          className={cn(
            "flex items-center gap-2 rounded-full border px-3 py-1.5 text-body-sm font-semibold tabular-nums",
            everyFindingAnswered
              ? "border-master/40 bg-master-tint text-master"
              : "border-border bg-surface-sunken text-text-2",
          )}
        >
          <Check
            className={cn("size-4", everyFindingAnswered ? "text-master" : "text-text-3")}
            aria-hidden="true"
          />
          {respondedCount} / {total} reviewed
        </div>
      </div>

      <div className="flex w-full flex-col gap-6 lg:flex-row">
        <FocusedLabel
          documentUrl={documentUrl}
          findings={findings}
          activeId={current.id}
          occurrenceIndex={occurrenceIndex}
          isDone={isDone}
        />

        <aside className="flex w-full flex-col gap-4 lg:w-[380px] lg:shrink-0">
          {/* Jump rail — every step at a glance, green once reviewed. */}
          <nav aria-label="Findings" className="flex flex-wrap items-center justify-center gap-2">
            {findings.map((f, i) => {
              const stepDone = f.clientResolution === "done"
              const stepDisputed = f.clientResolution === "disputed"
              const state = stepDone ? ", reviewed" : stepDisputed ? ", disputed" : ""
              return (
                <button
                  key={f.id}
                  type="button"
                  onClick={() => {
                    go(i)
                  }}
                  aria-current={i === safeIndex ? "step" : undefined}
                  aria-label={`Finding ${String(f.number)}${state}: ${f.title}`}
                  title={f.title}
                  className={cn(
                    "grid size-8 place-items-center rounded-full text-caption font-bold tabular-nums transition-colors duration-120",
                    "focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[color:var(--brand)]",
                    stepDone
                      ? "bg-master text-white"
                      : stepDisputed
                        ? "bg-info text-white"
                        : "bg-surface-sunken text-text-3 hover:bg-border",
                    i === safeIndex && "ring-2 ring-brand ring-offset-1 ring-offset-surface",
                  )}
                >
                  {stepDone ? (
                    <Check className="size-4" strokeWidth={3} aria-hidden="true" />
                  ) : stepDisputed ? (
                    <MessageSquareWarning className="size-4" aria-hidden="true" />
                  ) : (
                    f.number
                  )}
                </button>
              )
            })}
          </nav>

          <FindingCard
            finding={current}
            occurrenceIndex={Math.min(occurrenceIndex, Math.max(0, current.locations.length - 1))}
            onOccurrenceChange={(next) => {
              setOccurrenceIndex(
                Math.min(Math.max(next, 0), Math.max(0, current.locations.length - 1)),
              )
            }}
            done={done}
            onToggleDone={
              onSetResolution
                ? () => {
                    onSetResolution(current.id, done ? null : "done")
                  }
                : undefined
            }
            disputed={disputed}
            onToggleDisputed={
              onSetResolution
                ? () => {
                    onSetResolution(current.id, disputed ? null : "disputed")
                    // A dispute is an argument; open the thread so it gets made.
                    if (!disputed && !showMessages && onSendMessage) toggleMessages()
                  }
                : undefined
            }
            showMessages={showMessages}
            onToggleMessages={toggleMessages}
            thread={messagesFor(current.id)}
            unread={unreadFor(current.id)}
            onSend={
              onSendMessage
                ? (body) => {
                    onSendMessage(current.id, body)
                  }
                : undefined
            }
          />

          <div className="flex items-center justify-between gap-3">
            <Button
              size="sm"
              disabled={safeIndex === 0}
              onClick={() => {
                go(safeIndex - 1)
              }}
              leadingIcon={<ChevronLeft aria-hidden="true" />}
            >
              Previous
            </Button>
            <span className="text-caption font-medium tabular-nums text-text-3">
              Finding {safeIndex + 1} / {total}
            </span>
            <Button
              size="sm"
              variant="primary"
              disabled={safeIndex === total - 1}
              onClick={() => {
                go(safeIndex + 1)
              }}
              trailingIcon={<ChevronRight aria-hidden="true" />}
            >
              Next
            </Button>
          </div>

          {everyFindingAnswered && onResubmit && (
            <ResubmitPanel
              onResubmit={onResubmit}
              pending={resubmitPending}
              submitted={resubmitted}
              error={resubmitError}
            />
          )}
        </aside>
      </div>
    </div>
  )
}

/**
 * The walkthrough's last step: hand back the corrected label, which opens the
 * next version of this review in the same thread. Nothing on this version
 * changes.
 */
function ResubmitPanel({
  onResubmit,
  pending,
  submitted,
  error,
}: {
  onResubmit: (file: File) => void
  pending: boolean
  submitted: boolean
  error: string | null
}): JSX.Element {
  const fileInputRef = React.useRef<HTMLInputElement>(null)
  // The chosen file is what gets uploaded on confirm, not render data.
  const pendingFileRef = React.useRef<File | null>(null)
  const [pendingName, setPendingName] = React.useState<string | null>(null)
  const [confirmOpen, setConfirmOpen] = React.useState(false)

  return (
    <div className="rounded-lg border border-dashed border-border-strong bg-surface-sunken p-4 text-center">
      {submitted ? (
        <p className="flex items-center justify-center gap-2 text-body-sm font-medium text-master">
          <CheckCircle2 className="size-4" aria-hidden="true" /> Corrected label received — a new
          version is being reviewed
        </p>
      ) : (
        <>
          <p className="text-body-sm font-medium text-text">All findings reviewed</p>
          <p className="mt-1 text-caption text-text-3">
            Upload your corrected label and we will re-review it against the same masters, carrying
            these findings forward.
          </p>
        </>
      )}
      <input
        ref={fileInputRef}
        type="file"
        accept="application/pdf,.pdf"
        className="hidden"
        aria-label="Corrected label (PDF)"
        onChange={(event) => {
          const file = event.target.files?.[0]
          if (file) {
            pendingFileRef.current = file
            setPendingName(file.name)
            setConfirmOpen(true)
          }
          event.target.value = ""
        }}
      />
      <Button
        size="sm"
        variant="primary"
        className="mt-3"
        loading={pending}
        leadingIcon={<Upload aria-hidden="true" />}
        onClick={() => fileInputRef.current?.click()}
      >
        {submitted ? "Upload a different document" : "Upload corrected label"}
      </Button>
      {error && (
        <p role="alert" className="mt-2 text-caption text-err">
          {error}
        </p>
      )}

      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent
          size="sm"
          title="Submit the corrected label?"
          description={
            <>
              <span className="font-medium text-text">{pendingName ?? "Your file"}</span> will be
              compared against this version&apos;s findings and the same approved masters. A new
              version of this review opens; nothing on this version changes.
            </>
          }
          footer={
            <>
              <Button
                variant="ghost"
                onClick={() => {
                  setConfirmOpen(false)
                }}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                onClick={() => {
                  const file = pendingFileRef.current
                  setConfirmOpen(false)
                  if (file) onResubmit(file)
                }}
              >
                Submit for re-review
              </Button>
            </>
          }
        />
      </Dialog>
    </div>
  )
}
